import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { apiFetch, ApiError } from '@/api/client';
import { getFinding, listFindingEvents, listFindingsForRun } from '@/api/findings';
import { markNoInvoice, unmarkNoInvoice } from '@/api/invoices';
import { getRun } from '@/api/runs';
import { listRules } from '@/api/rules';
import { listSupplierBnPeriods } from '@/api/suppliers';
import type { FindingListOut, FindingOut, RuleOut } from '@/api/types';
import { AttachInvoicePanel } from '@/components/AttachInvoicePanel';
import { ClaimedGstPanel } from '@/components/ClaimedGstPanel';
import { FindingDispositionPanel } from '@/components/FindingDispositionPanel';
import { FindingTags } from '@/components/FindingTags';
import { ExplainFinding } from '@/components/ExplainFinding';
import { GoverningAuthority } from '@/components/GoverningAuthority';
import { InvoicePreview } from '@/components/InvoicePreview';
import { SuggestDisposition } from '@/components/SuggestDisposition';
import { distinctSupplierNames, invoiceFactsPrefill } from '@/lib/invoicePrefill';
import { JournalEntryPanel } from '@/components/JournalEntryPanel';
import { ReasonCodePills } from '@/components/ReasonCodesPicker';
import { RecoveryAmountEditor, RecoveryHistoryPanel } from '@/components/RecoveryAmountEditor';
import { useClaimedLines } from '@/hooks/useClaimedLines';
import { useTransactionDocuments } from '@/hooks/useTransactionDocuments';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { APPROVAL_REASON_META, defaultApprovalReason } from '@/lib/approval-reasons';
import { historyBackIsNoop } from '@/lib/backNavigation';
import { bnForDate } from '@/lib/bnPeriods';
import { dispositionTrail } from '@/lib/dispositionHistory';
import {
  showsFinding,
  showsInvoice,
  splitContainerClass,
  togglePanel,
  type SplitExpand,
} from '@/lib/findingSplitLayout';
import {
  readVisibleFindingOrder,
  visibleFindingOrderKey,
} from '@/lib/findingFilterStorage';
import { findingNeighbors } from '@/lib/findingNeighbors';
import { coerceCurrency, formatCurrency, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import { useAuth } from '../auth/AuthContext';

/** /runs/$id/findings/$findingId — standalone read-only finding detail.
 *
 *  Reached from the supplier page's Findings table (click a row).
 *  Mirrors the route shape of `runs.$id.suppliers.$supplierName.tsx`:
 *  same Back-button-driven navigation, same URL-as-state pattern.
 *
 *  A deep-dive view of one finding — the full evidence dict + journal
 *  entry for NetSuite-source runs, the invoice, and the disposition
 *  history. Practitioners can also Approve / Reject / flag Needs-review
 *  here (FindingDispositionPanel), so a finding can be triaged without
 *  bouncing back to the review-queue card.
 */
export function FindingDetailPage() {
  const { id, findingId } = useParams({ from: '/runs/$id/findings/$findingId' });
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();

  // Fetch just this finding by id (INP-270) instead of pulling the whole
  // run's finding list to `.find()` one row. A direct navigation (cold
  // cache — the slow path) then loads one small payload rather than up to
  // 5000 findings, so the card + invoice + journal render immediately.
  // When the practitioner arrived from the Review queue / Suppliers tab
  // the list is already cached, so we seed `initialData` from it for an
  // instant first paint (a background refetch still picks up fresh edits).
  const findingQuery = useQuery({
    queryKey: ['finding', findingId, userKey],
    enabled: isSignedIn,
    queryFn: async () => getFinding(findingId, (await getIdToken()) ?? ''),
    initialData: () =>
      qc
        .getQueryData<FindingListOut>(['findings', 'by-run', id, userKey])
        ?.items.find((f) => f.finding_id === findingId),
  });
  const rulesQuery = useQuery({
    queryKey: ['rules', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRules((await getIdToken()) ?? ''),
  });
  // Same cache key as the run detail page, so the breadcrumb's customer /
  // engagement labels come from cache when the user arrived here from the
  // run (Recovery) page — no extra request in the common path.
  const runQuery = useQuery({
    queryKey: ['runs', id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getRun(id, (await getIdToken()) ?? ''),
  });
  const run = runQuery.data;

  const findingNotFound =
    findingQuery.isError && findingQuery.error instanceof ApiError && findingQuery.error.status === 404;
  // A finding belongs to exactly one run, but `getFinding` is keyed only by
  // id — so reject a fetched/seeded finding whose `audit_run_id` doesn't match
  // the URL's run (it must not render under another run's breadcrumbs, invoice
  // scope, or journal lookup), and treat a 404 as not-found even when stale
  // `initialData` is still in the cache. Both collapse to the "not found"
  // branch below.
  const rawFinding = findingQuery.data ?? null;
  const finding = !findingNotFound && rawFinding?.audit_run_id === id ? rawFinding : null;
  const rule = useMemo(
    () => rulesQuery.data?.items.find((r) => r.id === finding?.rule_id) ?? null,
    [rulesQuery.data, finding],
  );
  // The run's full finding list, fetched in the background ONLY to build the
  // invoice editor's supplier-name autocomplete (#481). Deliberately does not
  // gate the page — the card + invoice + journal render off `findingQuery`
  // above — so the autocomplete options just fill in a moment later. Shares
  // the Review queue / Suppliers cache key, so it's a hit when the
  // practitioner navigated from there.
  const findingsQuery = useQuery({
    queryKey: ['findings', 'by-run', id, userKey],
    enabled: isSignedIn,
    queryFn: async () => listFindingsForRun(id, (await getIdToken()) ?? ''),
  });
  // Distinct supplier names across the run — the searchable options for
  // the invoice editor's supplier field.
  const supplierNameOptions = useMemo(
    () => distinctSupplierNames(findingsQuery.data?.items ?? []),
    [findingsQuery.data],
  );

  // Prev/Next stepping through the list view's persisted order, so the
  // practitioner can walk every finding without returning to the list. Read
  // once per run; the position recomputes as `findingId` changes.
  const navigate = useNavigate();
  const visibleOrder = useMemo(() => readVisibleFindingOrder(visibleFindingOrderKey(id)), [id]);
  const neighbors = useMemo(
    () => findingNeighbors(visibleOrder, findingId),
    [visibleOrder, findingId],
  );
  const goToFinding = useCallback(
    (targetId: string) => {
      void navigate({
        to: '/runs/$id/findings/$findingId',
        params: { id, findingId: targetId },
      });
    },
    [navigate, id],
  );
  // `history.back()` silently no-ops when this is the tab's first entry
  // (finding opened in a new tab, deep-linked, or reloaded). When there's no
  // back target, replace to the run page — a push would leave the dead-end
  // finding underneath. Otherwise return to exactly where the practitioner
  // came from (list/audit tab + scroll).
  const goBack = useCallback(() => {
    const entryIndex = (window.history.state as { __TSR_index?: number } | null)
      ?.__TSR_index;
    if (historyBackIsNoop(entryIndex)) {
      void navigate({ to: '/runs/$id', params: { id }, replace: true });
    } else {
      window.history.back();
    }
  }, [navigate, id]);
  const { prevId, nextId } = neighbors;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      // Don't hijack the arrows while the practitioner is typing/selecting.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowLeft' && prevId) {
        e.preventDefault();
        goToFinding(prevId);
      } else if (e.key === 'ArrowRight' && nextId) {
        e.preventDefault();
        goToFinding(nextId);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prevId, nextId, goToFinding]);

  return (
    // Full-width outer so the journal entry below can span the page and
    // scroll horizontally; the finding info + invoice keep a readable
    // max-w. grid-cols-1 gives a minmax(0,1fr) track so a wide child
    // can't blow the layout open past the viewport.
    <div className="grid min-w-0 grid-cols-1 gap-4">
      <header className="grid gap-2">
        <Breadcrumbs>
          <Link to="/customers" className="text-brand-royal underline-offset-4 hover:underline">
            Customers
          </Link>
          {/* Parent links only render for engagement-linked runs; CLI-triggered
              runs have no customer/engagement context (the API ships null for
              those fields). Mirrors the run (Recovery) page breadcrumb. */}
          {run?.customer_slug && run?.customer_name && (
            <Link
              to="/customers/$slug"
              params={{ slug: run.customer_slug }}
              className="text-brand-royal underline-offset-4 hover:underline"
            >
              {run.customer_name}
            </Link>
          )}
          {run?.engagement_id && run?.engagement_name && (
            <Link
              to="/engagements/$id"
              params={{ id: run.engagement_id }}
              className="text-brand-royal underline-offset-4 hover:underline"
            >
              {run.engagement_name}
            </Link>
          )}
          <Link
            to="/runs/$id"
            params={{ id }}
            className="text-brand-royal underline-offset-4 hover:underline"
          >
            Recovery
          </Link>
          <span className="text-brand-navy">Finding</span>
        </Breadcrumbs>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex w-fit items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1 text-sm text-brand-royal shadow-xs hover:bg-muted/50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            aria-label="Go back to previous page"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
          {/* Step through the list's order. Hidden when this finding isn't in
              the persisted order (deep link, or the filter changed since). */}
          {neighbors.index >= 0 && (
            <div className="ml-auto flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={prevId === null}
                onClick={() => prevId && goToFinding(prevId)}
                aria-label="Previous finding (left arrow)"
                title="Previous finding (←)"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Prev
              </Button>
              <span className="min-w-16 text-center text-xs tabular-nums text-brand-slate">
                {neighbors.index + 1} / {neighbors.total}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={nextId === null}
                onClick={() => nextId && goToFinding(nextId)}
                aria-label="Next finding (right arrow)"
                title="Next finding (→)"
              >
                Next
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </header>

      {findingQuery.isPending || rulesQuery.isPending ? (
        <p className="text-sm text-brand-slate">Loading finding…</p>
      ) : findingQuery.isError && !findingQuery.data && !findingNotFound ? (
        // Only hard-fail when there's no cached data. A background refetch
        // that 401s during a Clerk session refresh must not unmount the page
        // (and the in-progress invoice editor with it) — keep the last-good
        // data on screen (INP-502). A 404 falls through to the "not found"
        // card below rather than the generic error.
        <p className="text-sm text-destructive">Couldn't load this finding — try refreshing.</p>
      ) : rulesQuery.isError && !rulesQuery.data ? (
        <p className="text-sm text-destructive">
          Couldn't load the rule catalog — try refreshing.
        </p>
      ) : finding === null ? (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle>Finding not found</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-brand-slate">
              No finding with id <span className="font-mono">{findingId}</span> on this
              run. It may have been deleted or the URL is stale.
            </p>
          </CardContent>
        </Card>
      ) : (
        <FindingDetailBody
          runId={id}
          finding={finding}
          rule={rule}
          supplierNameOptions={supplierNameOptions}
        />
      )}
    </div>
  );
}

function FindingDetailBody({
  runId,
  finding,
  rule,
  supplierNameOptions,
}: {
  runId: string;
  finding: FindingOut;
  rule: RuleOut | null;
  supplierNameOptions: string[];
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  // Fetch the run for its customer slug (invoice download + link lookup are
  // scoped by customer). Same query key as the run page, so this is a cache
  // hit when the practitioner navigated here from there.
  const runQuery = useQuery({
    queryKey: ['runs', runId, userKey],
    enabled: isSignedIn,
    queryFn: async () => getRun(runId, (await getIdToken()) ?? ''),
  });
  const customerSlug = runQuery.data?.customer_slug ?? null;
  const {
    linksByTransaction,
    extractionsByDocument,
    downloadUrlsByDocument,
    markedTransactions,
    linksReady,
  } = useTransactionDocuments(customerSlug, [finding.transaction_id], { withDownload: true });
  // Filed-claim lines for this transaction (deterministic joins only) —
  // the claim-gap view. Renders nothing for customers with no claim
  // data loaded (claimPeriodCount === 0).
  const { claimsByTransaction, claimPeriodCount, claimsReady } = useClaimedLines(
    customerSlug,
    [finding.transaction_id],
  );

  // Same `['me', userKey]` key the run page uses — a cache hit when the
  // practitioner arrived from there. Only practitioners can triage a
  // transaction as "no invoice available".
  const me = useQuery({
    queryKey: ['me', userKey],
    enabled: isSignedIn,
    queryFn: async () =>
      apiFetch<{ id: string; user_type: 'practitioner' | 'customer' }>('/api/me', {
        idToken: (await getIdToken()) ?? '',
      }),
  });
  const canReview = me.data?.user_type === 'practitioner';

  // The supplier's BN history, so the invoice editor can default the BN
  // from the registry — the run-time evidence snapshot often lacks a BN
  // that was captured after the run.
  const bnPeriodsQuery = useQuery({
    queryKey: ['supplier-bn-periods', finding.supplier_id, userKey],
    enabled: isSignedIn && finding.supplier_id !== null,
    queryFn: async () =>
      listSupplierBnPeriods(finding.supplier_id ?? '', (await getIdToken()) ?? ''),
  });

  const invoiceLinks = linksByTransaction.get(finding.transaction_id);
  const hasInvoice = (invoiceLinks?.length ?? 0) > 0;
  const isMarkedNoInvoice = markedTransactions.has(finding.transaction_id);

  // The "no invoice available" mark (INP-494) — a transaction-level triage
  // state, orthogonal to disposition. Toggling refreshes the attachment
  // lookup so the pill here and the badges on the run page stay in sync.
  const noInvoiceMark = useMutation({
    mutationFn: async (marked: boolean) => {
      const token = (await getIdToken()) ?? '';
      const slug = customerSlug ?? '';
      return marked
        ? markNoInvoice(slug, finding.transaction_id, token)
        : unmarkNoInvoice(slug, finding.transaction_id, token);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transaction-documents', customerSlug] });
    },
  });

  // Evidence keys mirror the rule's `evidence_required` field-path list, which
  // emits flat names for top-level Transaction fields (`bill_date`, `bill_amount`,
  // `tax_amount`) and dotted names only for nested ones (`supplier.name`,
  // `gl_account.name`). An earlier version of this page read `bill.date` /
  // `bill.amount` / `bill.tax_amount` (dotted), which never resolved — Andrew
  // saw `—` for "Amount paid" and "Tax paid" on every finding.
  const supplierName = stringOrNull(finding.evidence['supplier.name']);
  const billDate = stringOrNull(finding.evidence['bill_date']);
  // Prefill invoice facts from the GL transaction, but default the BN from
  // the supplier registry (applicable at the bill date) when the evidence
  // snapshot didn't capture one — the editor fills blanks only, so a saved
  // or per-invoice-overridden BN always wins.
  const evidencePrefill = invoiceFactsPrefill(finding.evidence);
  const invoicePrefill = {
    ...evidencePrefill,
    supplier_bn:
      evidencePrefill.supplier_bn ??
      (bnPeriodsQuery.data ? bnForDate(bnPeriodsQuery.data.items, billDate) : null),
  };
  const billAmount = stringOrNull(finding.evidence['bill_amount']);
  const taxAmount = stringOrNull(finding.evidence['tax_amount']);
  const glAccount = stringOrNull(finding.evidence['gl_account.name']);
  // Currency for the evidence amounts is the finding's currency — the engine
  // copies it off the source transaction, so `bill` + `tax` + `recovery` all
  // share it on a single GL row.
  const findingCurrency = coerceCurrency(finding.recovery.currency);
  const txType = stringOrNull(finding.evidence['transaction_type']);
  const province = stringOrNull(finding.evidence['supplier.province']);
  const effRate = stringOrNull(finding.evidence['implied_tax_rate']);
  const expRate = stringOrNull(finding.evidence['expected_tax_rate']);

  // Side-by-side finding + invoice; either can be expanded to full width.
  const [expanded, setExpanded] = useState<SplitExpand>('split');

  return (
    <>
      <div className={splitContainerClass(expanded)}>
        <section className={showsFinding(expanded) ? 'grid min-w-0 gap-2' : 'hidden'}>
          <PanelHeader
            label="Finding"
            expanded={expanded === 'finding'}
            onToggle={() => setExpanded(togglePanel(expanded, 'finding'))}
          />
          <Card>
            <CardHeader className="grid gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <CardTitle className="flex flex-wrap items-baseline gap-2 text-2xl font-semibold tracking-tight text-brand-navy">
                  <RecoveryAmountEditor
                    finding={finding}
                    billAmount={billAmount}
                    amountClassName={
                      finding.outcome === 'compliance_risk' ? 'text-amber-700' : 'text-emerald-700'
                    }
                  />
                  <span className="text-sm font-normal text-brand-slate">
                    {finding.outcome === 'compliance_risk' ? 'compliance risk' : 'recoverable'}
                  </span>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <FindingTags finding={finding} />
                  <CarriedForwardPill finding={finding} />
                  <StatusBadge kind="finding" status={finding.status} />
                </div>
              </div>
              {supplierName && (
                <p className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="text-xs uppercase tracking-wide text-brand-slate">
                    Supplier
                  </span>
                  <Link
                    to="/runs/$id/suppliers/$supplierName"
                    params={{ id: runId, supplierName }}
                    className="font-medium text-brand-navy underline-offset-4 hover:text-brand-royal hover:underline"
                  >
                    {supplierName}
                  </Link>
                </p>
              )}
              <RecoveryHistoryPanel finding={finding} />
            </CardHeader>

            <CardContent className="grid gap-5">
              {rule && (
                <section className="grid gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-brand-slate">Rule</p>
                  <p className="font-medium text-brand-navy">
                    {rule.aa_code} — {rule.title}
                  </p>
                  {rule.summary && (
                    <p className="text-sm text-brand-slate">{rule.summary}</p>
                  )}
                </section>
              )}

              <GoverningAuthority ruleId={finding.rule_id} />

              <ExplainFinding findingId={finding.finding_id} />

              <SuggestDisposition findingId={finding.finding_id} />

              <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat
                  label="Amount paid"
                  value={billAmount ? formatCurrency(billAmount, findingCurrency) : '—'}
                />
                <Stat
                  label="Tax paid"
                  value={taxAmount ? formatCurrency(taxAmount, findingCurrency) : '—'}
                />
                <Stat
                  label="Effective rate"
                  value={effRate !== null ? formatPct(effRate) : '—'}
                />
                <Stat
                  label="Expected rate"
                  value={expRate !== null ? formatPct(expRate) : '—'}
                />
                <Stat label="Province" value={province ?? '—'} />
                <Stat label="Date" value={billDate ?? '—'} />
                <Stat label="Transaction type" value={txType ?? '—'} />
                <Stat label="Tx id" value={finding.transaction_id} mono />
                {glAccount && (
                  <div className="col-span-2 sm:col-span-4">
                    <p className="text-xs uppercase tracking-wide text-brand-slate">
                      GL account
                    </p>
                    <p className="mt-0.5 break-words text-brand-navy">{glAccount}</p>
                  </div>
                )}
                {finding.memo_text?.trim() && (
                  <div className="col-span-2 sm:col-span-4">
                    <p className="text-xs uppercase tracking-wide text-brand-slate">
                      Memo
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap text-brand-navy">
                      {finding.memo_text.trim()}
                    </p>
                  </div>
                )}
              </section>

              <ClaimedGstPanel
                claims={claimsByTransaction.get(finding.transaction_id)}
                periodCount={claimPeriodCount}
                ready={claimsReady}
                observedTax={taxAmount}
                currency={findingCurrency}
              />

              <FindingDispositionPanel
                // Remount per finding so no disposition state (approval basis,
                // note, reject codes) carries across Prev/Next navigation,
                // which keeps this panel mounted while only `finding` changes.
                key={finding.finding_id}
                finding={finding}
                runId={runId}
                canReview={canReview}
                defaultApprovalReason={defaultApprovalReason(invoiceLinks)}
              />

              {finding.status !== 'pending' &&
                (finding.approval_note ||
                  finding.approval_reason ||
                  finding.rejection_reason ||
                  finding.review_note ||
                  (finding.reason_codes && finding.reason_codes.length > 0) ||
                  finding.reviewer_alias) && (
                  <section className="grid gap-1.5 rounded-md border border-border bg-muted/30 px-3 py-2">
                    <p className="text-xs uppercase tracking-wide text-brand-slate">
                      Disposition
                    </p>
                    {(finding.approval_note ||
                      finding.rejection_reason ||
                      finding.review_note) && (
                      <p className="whitespace-pre-wrap text-sm text-brand-navy">
                        {finding.approval_note ??
                          finding.rejection_reason ??
                          finding.review_note}
                      </p>
                    )}
                    {finding.reason_codes && finding.reason_codes.length > 0 && (
                      <div className="flex flex-wrap items-baseline gap-2 text-sm">
                        <span className="text-brand-slate">Codes:</span>
                        <ReasonCodePills codes={finding.reason_codes} />
                      </div>
                    )}
                    {finding.status === 'approved' && finding.approval_reason && (
                      <div
                        className="flex flex-wrap items-baseline gap-2 text-sm"
                        title={APPROVAL_REASON_META[finding.approval_reason].tooltip}
                      >
                        <span className="text-brand-slate">Basis:</span>
                        <span className="text-brand-navy">
                          {APPROVAL_REASON_META[finding.approval_reason].label}
                        </span>
                      </div>
                    )}
                    {finding.reviewer_alias && (
                      <p className="text-xs text-brand-slate">
                        by {finding.reviewer_alias}
                        {finding.reviewed_at && <> · {formatDateTime(finding.reviewed_at)}</>}
                      </p>
                    )}
                    <DispositionHistory
                      findingId={finding.finding_id}
                      status={finding.status}
                    />
                  </section>
                )}
            </CardContent>
          </Card>
        </section>
        <section className={showsInvoice(expanded) ? 'grid min-w-0 gap-2' : 'hidden'}>
          <PanelHeader
            label="Invoice"
            expanded={expanded === 'invoice'}
            onToggle={() => setExpanded(togglePanel(expanded, 'invoice'))}
          />
          <div className="grid gap-4">
            <InvoicePreview
              customerSlug={customerSlug}
              links={invoiceLinks}
              extractionsByDocument={extractionsByDocument}
              downloadUrlsByDocument={downloadUrlsByDocument}
              prefill={invoicePrefill}
              supplierNameOptions={supplierNameOptions}
            />
            <AttachInvoicePanel
              customerSlug={customerSlug}
              transactionId={finding.transaction_id}
            />
            {/* No-invoice triage (INP-494): only meaningful when nothing is
                attached — marking "no invoice available" takes the transaction
                off the collection worklist. */}
            {linksReady && !hasInvoice && customerSlug && (
              <div className="flex flex-wrap items-center gap-2">
                {isMarkedNoInvoice ? (
                  <>
                    <span
                      className="inline-flex items-center rounded border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium text-brand-slate"
                      title="This transaction has no supporting invoice"
                    >
                      No invoice available
                    </span>
                    {canReview && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={noInvoiceMark.isPending}
                        onClick={() => noInvoiceMark.mutate(false)}
                      >
                        Undo
                      </Button>
                    )}
                  </>
                ) : (
                  canReview && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={noInvoiceMark.isPending}
                      onClick={() => noInvoiceMark.mutate(true)}
                      title="Record that this transaction has no supporting invoice — takes it off the collection worklist."
                    >
                      Mark: no invoice available
                    </Button>
                  )
                )}
                {noInvoiceMark.isError && (
                  <span className="text-[11px] text-destructive">
                    Couldn&rsquo;t update — try again
                  </span>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Full width + min-w-0 so the wide NetSuite line set scrolls
          horizontally instead of pushing the page — same JournalEntryPanel
          the flashcard uses. */}
      <Card className="min-w-0">
        <CardContent className="pt-6">
          <JournalEntryPanel
            runId={runId}
            transactionId={finding.transaction_id}
            transactionIdUuid={finding.transaction_id_uuid}
          />
        </CardContent>
      </Card>
    </>
  );
}

/** Header above each split panel with a label and an expand / restore
 *  toggle. Expanding a panel gives it the full page width; the toggle then
 *  restores the balanced side-by-side view. */
function PanelHeader({
  label,
  expanded,
  onToggle,
}: {
  label: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">{label}</h3>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onToggle}
        className="h-7 gap-1 text-xs"
        aria-label={expanded ? `Restore split view` : `Expand ${label.toLowerCase()}`}
      >
        {expanded ? (
          <>
            <Minimize2 className="h-3.5 w-3.5" aria-hidden="true" />
            Split view
          </>
        ) : (
          <>
            <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
            Expand
          </>
        )}
      </Button>
    </div>
  );
}

function Stat({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    // min-w-0 lets the grid track shrink; break-all keeps a long id (or any
    // unbreakable value) from forcing the whole grid — and the page — wider.
    <div className="grid min-w-0 gap-0.5">
      <p className="text-[11px] uppercase tracking-wide text-brand-slate">{label}</p>
      <p
        className={`text-sm font-medium text-brand-navy ${mono ? 'break-all font-mono' : 'break-words'}`}
      >
        {value}
      </p>
    </div>
  );
}

function stringOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  return null;
}

function formatPct(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `${(n * 100).toFixed(2)}%`;
}

function CarriedForwardPill({ finding }: { finding: FindingOut }) {
  if (!finding.auto_carried_forward) return null;
  const sourceId = finding.carried_forward_from_finding_id;
  const reviewer = finding.reviewer_alias;
  const reviewed = finding.reviewed_at
    ? new Date(finding.reviewed_at).toLocaleDateString()
    : null;
  const lines = [
    'Disposition auto-carried from a prior run.',
    reviewer && reviewed ? `${finding.status} by ${reviewer} on ${reviewed}.` : null,
    sourceId ? `Source finding: ${sourceId.slice(0, 12)}…` : null,
    'Approve / reject / unreview clears this flag.',
  ].filter(Boolean) as string[];
  return (
    <Badge
      variant="outline"
      title={lines.join('\n')}
      className="border-brand-slate/30 bg-muted/40 font-normal text-brand-slate"
    >
      ↩ carried
    </Badge>
  );
}

/** The finding's disposition trail — "brian approved → rejected · invoice
 *  shows no tax". Renders nothing until there's more than a single
 *  transition (a plain approve/reject is already told by the badge). */
function DispositionHistory({
  findingId,
  status,
}: {
  findingId: string;
  status: string;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const events = useQuery({
    queryKey: ['finding-events', findingId, status, userKey],
    enabled: isSignedIn && status !== 'pending',
    queryFn: async () => listFindingEvents(findingId, (await getIdToken()) ?? ''),
  });
  const trail = useMemo(() => dispositionTrail(events.data?.items ?? []), [events.data]);
  if (trail.length < 2) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs text-brand-slate">
      {trail.map((seg, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <span aria-hidden="true">→</span>}
          <span
            className={cn(
              'rounded px-1.5 py-0.5',
              seg.toStatus === 'rejected' && 'bg-destructive/10 text-destructive',
              seg.toStatus === 'approved' && 'bg-emerald-50 text-emerald-800',
              seg.toStatus === 'needs_review' && 'bg-amber-50 text-amber-900',
              seg.toStatus === 'pending' && 'bg-muted text-brand-slate',
            )}
          >
            {seg.actor ? `${seg.actor} ` : ''}
            {seg.verb}
            {seg.note ? ` · ${seg.note}` : ''}
          </span>
        </span>
      ))}
    </div>
  );
}
